/**
 * PII marker (spec 68 §8.2).
 *
 * Wrapping a value in `pii(value)` tells the attribute scrubber the value
 * was *intentionally* included and should pass through the redaction
 * patterns unchanged. Without the marker, anything matching a redaction
 * rule in §8.1 is replaced with `<redacted*>`.
 *
 * Compliance carve-out (§8.1): credit-card-shaped numbers, `Bearer …`
 * tokens and `c0a_…` secrets are ALWAYS scrubbed — `pii()` does not
 * override them. That enforcement lives in log/redact.ts; this module
 * only carries the marker.
 */

const PII_BRAND = Symbol.for('@repo/telemetry/pii');

export interface Pii<T> {
  readonly [PII_BRAND]: true;
  readonly value: T;
}

/**
 * Mark a value as an intentional PII inclusion. The scrubber unwraps it
 * and (subject to the compliance carve-out) emits the raw value.
 */
export function pii<T>(value: T): Pii<T> {
  return { [PII_BRAND]: true, value };
}

/** Type guard: is this value a `pii()`-wrapped marker? */
export function isPii(value: unknown): value is Pii<unknown> {
  return typeof value === 'object' && value !== null && (value as Record<symbol, unknown>)[PII_BRAND] === true;
}

/** Unwrap a `pii()` marker to its inner value. */
export function unwrapPii<T>(marker: Pii<T>): T {
  return marker.value;
}
